// ===================================================================
// src/app/lib/notificationSocket.ts
//
// Canal WS por-usuário (não por-grupo/board) — avisa em tempo real sobre
// qualquer Notification criada para o usuário logado (card novo no setor,
// atribuição, comentário, etc.), mesmo quando a página/board relevante não
// está aberta no momento. Protocolo: ws(s)://<api-host>/ws/notifications/
// ?token=<access_token> (apps/notifications/consumers.py::NotificationConsumer
// + apps/notifications/routing.py no eleveia-api).
//
// Cópia estrutural de chatNotifySocket.ts — instância única pro app inteiro,
// conectada em AppLayout.tsx. Canal só-leitura: não existe `send()`.
// ===================================================================

import { getAccessToken } from './api';
import type { NotificationItem } from '../services/notifications';

export type NotificationSocketEventType = 'open' | 'notification.new';

type EventHandler<T = unknown> = (payload: T) => void;

const RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_ATTEMPTS = 5;
// 4001 = não autenticado (sem token) — não faz sentido tentar de novo sem
// uma sessão válida.
const PERMANENT_FAILURE_CODES = new Set([4001]);

function buildWsUrl(token: string): string {
  const rawBase = (import.meta.env.VITE_API_URL ?? 'http://localhost:8000').replace(/\/+$/, '');
  const wsProtocol = rawBase.startsWith('https') ? 'wss' : 'ws';
  const hostAndPort = rawBase.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${hostAndPort}/ws/notifications/?token=${encodeURIComponent(token)}`;
}

function createNotificationSocket() {
  let ws: WebSocket | null = null;
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let manuallyDisconnected = true;

  const handlers = new Map<string, Set<EventHandler<any>>>();

  function emit(type: string, payload: unknown) {
    handlers.get(type)?.forEach((h) => h(payload));
  }

  function clearReconnectTimer() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function open() {
    const token = getAccessToken();
    if (!token) return;

    const socket = new WebSocket(buildWsUrl(token));

    socket.onopen = () => {
      reconnectAttempts = 0;
      emit('open', undefined);
    };

    socket.onmessage = (event) => {
      let data: { type?: string; payload?: unknown };
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!data.type) return;
      emit(data.type, data.payload);
    };

    socket.onclose = (event) => {
      ws = null;
      if (manuallyDisconnected) return;
      if (PERMANENT_FAILURE_CODES.has(event.code) || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;

      reconnectAttempts += 1;
      clearReconnectTimer();
      reconnectTimer = setTimeout(open, RECONNECT_DELAY_MS);
    };

    socket.onerror = () => {
      // onclose é sempre disparado logo em seguida — reconexão fica lá.
    };

    ws = socket;
  }

  return {
    /** Idempotente — chamar de novo com uma conexão já aberta não faz nada. */
    connect() {
      if (ws) return;
      manuallyDisconnected = false;
      reconnectAttempts = 0;
      open();
    },

    disconnect() {
      manuallyDisconnected = true;
      clearReconnectTimer();
      if (ws) {
        ws.onclose = null;
        ws.close(1000, 'client disconnect');
        ws = null;
      }
    },

    on(type: NotificationSocketEventType, handler: EventHandler<any>) {
      if (!handlers.has(type)) handlers.set(type, new Set());
      handlers.get(type)!.add(handler);
    },

    off(type: string, handler: EventHandler<any>) {
      handlers.get(type)?.delete(handler);
    },
  };
}

/** Instância única compartilhada por todo o app — conectada em AppLayout.tsx. */
export const notificationSocket = createNotificationSocket();

export type { NotificationItem };
